import React from "react";

const URL_RE = /\bhttps?:\/\/[^\s<>]+|\bwww\.[^\s<>]+/gi;
const TRAILING_PUNCT = /[.,;:!?)\]}"'»」]+$/;

function stripTrailingPunct(s: string): [clean: string, trailing: string] {
  let trailing = "";
  let clean = s;
  // Strip balanced trailing punctuation; also pop a single trailing ')' only when
  // there's no '(' inside the URL (avoids breaking wiki-style links).
  for (;;) {
    const m = clean.match(TRAILING_PUNCT);
    if (!m) break;
    const last = clean.slice(-1);
    if (last === ")" && clean.includes("(")) break;
    trailing = last + trailing;
    clean = clean.slice(0, -1);
  }
  return [clean, trailing];
}

function ensureProtocol(url: string): string {
  return /^https?:\/\//i.test(url) ? url : `https://${url}`;
}

function renderTextWithBreaks(text: string, keyPrefix: string): React.ReactNode[] {
  if (!text) return [];
  const parts = text.split("\n");
  const out: React.ReactNode[] = [];
  parts.forEach((p, i) => {
    if (i > 0) out.push(<br key={`${keyPrefix}-br-${i}`} />);
    if (p) out.push(p);
  });
  return out;
}

interface Props {
  text?: string | null;
}

export default function Linkified({ text }: Props) {
  if (!text) return null;

  const nodes: React.ReactNode[] = [];
  let lastIndex = 0;
  let i = 0;
  const re = new RegExp(URL_RE.source, URL_RE.flags);
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    const [raw] = m;
    const start = m.index;
    if (start > lastIndex) {
      nodes.push(...renderTextWithBreaks(text.slice(lastIndex, start), `t-${i}`));
    }
    const [clean, trailing] = stripTrailingPunct(raw);
    if (clean.length === 0) {
      // Edge case: match is pure punctuation — bail out and treat as text.
      nodes.push(...renderTextWithBreaks(raw, `t-${i}-raw`));
    } else {
      nodes.push(
        <a
          key={`a-${i}`}
          href={ensureProtocol(clean)}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          className="task-link"
        >
          {clean}
        </a>,
      );
      if (trailing) nodes.push(...renderTextWithBreaks(trailing, `t-${i}-tr`));
    }
    lastIndex = start + raw.length;
    i++;
  }
  if (lastIndex < text.length) {
    nodes.push(...renderTextWithBreaks(text.slice(lastIndex), `t-${i}-tail`));
  }
  return <>{nodes}</>;
}
